import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";

interface EmailStepProps {
  userData: User | null;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

export function EmailStep({ userData, onSuccess, onError }: EmailStepProps) {
  const [email, setEmail] = React.useState(userData?.email ?? "");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Email tidak boleh kosong");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Format email tidak valid");
      return;
    }

    setIsLoading(true);
    try {
      await authService.updateEmail(email.trim());
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal menyimpan email";
      setError(msg);
      onError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
        <span className="mt-0.5 text-blue-600 dark:text-blue-400">ℹ</span>
        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
          Masukkan email yang akurat untuk komunikasi lebih lanjut.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="contoh@email.com"
          disabled={isLoading}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={isLoading || !email.trim()}>
          {isLoading ? "Menyimpan..." : "Lanjutkan"}
        </Button>
      </div>
    </form>
  );
}
